import { Box } from "@chakra-ui/react";
import { ColorIcon, NextLink, Spacing } from "goi_common";
import { BaseStack, baseColors } from "goi_common/src/design-system";

import { RegionParlorStats } from "@/utils/getRegionParlorStats";

/**
 * 지역 장례식장 현황 + 서비스별 평균 비용 통합 블록.
 * "몇 곳이 있고 비용이 어떻게 되는지"에 집중 - 시설 수(유형 칩) + 무빈소/가족장/일반장 평균(조문객 기준) + 가격순 링크.
 */
interface RegionStatsHighlightProps {
  regionLabel: string; // "송파구" | "서울"
  stats: RegionParlorStats;
  sidoName: string;
  sigunguName?: string;
}

/** managed_type 가시 표기 (실데이터: 전문/대학병원/공설 3종) */
const MANAGED_TYPE_LABEL: Record<string, string> = {
  전문: "전문장례식장",
  대학병원: "대학병원",
  공설: "공설",
};

/** 서비스별 표기·조문객 규모·URL 세그먼트 (기존 ParlorServiceSummary 표기 유지) */
const SERVICE_ROWS: Array<{
  label: string;
  guest: string;
  chargeKey: "무빈소" | "가족장" | "일반장";
  segment: string;
}> = [
  { label: "무빈소장례", guest: "조문객 없음", chargeKey: "무빈소", segment: "무빈소장" },
  { label: "가족장", guest: "조문객 50명 내외", chargeKey: "가족장", segment: "가족장" },
  { label: "일반장", guest: "조문객 200명 이상", chargeKey: "일반장", segment: "일반장" },
];

function RegionStatsHighlight({
  regionLabel,
  stats,
  sidoName,
  sigunguName,
}: RegionStatsHighlightProps) {
  if (stats.parlorCount === 0) return null;

  const managedChips = Object.entries(stats.managedTypeCounts)
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1]);

  const regionSegment = sigunguName ? `${sidoName}-${sigunguName}` : sidoName;

  return (
    <BaseStack width="100%" px={24}>
      <Box as="h2" className="headline_2_sb" color={baseColors.label_normal}>
        {regionLabel} 장례식장 현황
      </Box>
      <Spacing gap={12} />
      <Box
        width="100%"
        padding="16px"
        borderRadius="12px"
        background={baseColors.background_accent_1}
      >
        {/* 시설 수 + 유형 칩 */}
        <BaseStack direction="horizontal" alignItems="center" flexWrap="wrap">
          <Box className="body_1_normal_sb" color={baseColors.label_normal}>
            장례식장 {stats.parlorCount}곳
          </Box>
          {managedChips.map(([type, count]) => (
            <Box
              key={type}
              className="caption_1_m"
              color={baseColors.label_alternative_1}
              background={baseColors.fill_accent}
              borderRadius="999px"
              padding="3px 9px"
              whiteSpace="nowrap"
              marginLeft="6px"
            >
              {MANAGED_TYPE_LABEL[type] ?? type} {count}곳
            </Box>
          ))}
        </BaseStack>

        <Spacing gap={12} />
        <Box width="100%" height="1px" background={baseColors.line_accent} />
        <Spacing gap={4} />

        {/* 서비스별 평균 비용 (조문객 기준) + 가격순 링크 */}
        {SERVICE_ROWS.map(({ label, guest, chargeKey, segment }) => {
          const avg = stats.avgCharge?.[chargeKey] ?? 0;
          if (avg <= 0) return null;

          return (
            <NextLink
              key={segment}
              href={`/parlors/region/service/${regionSegment}/${segment}/`}
              event={{
                newGtm: {
                  path: "parlor_region",
                  trigger_and_target: "service_by_chip-uv_parlor_region_service",
                },
              }}
            >
              <Box display="flex" alignItems="center" width="100%" cursor="pointer" padding="8px 0">
                <Box minWidth={0} flex={1}>
                  <Box className="body_2_normal_sb" color={baseColors.label_normal}>
                    {label}
                  </Box>
                  <Box className="caption_1_r" color={baseColors.label_assistive_2}>
                    {guest}
                  </Box>
                </Box>
                <Box className="body_2_normal_sb" color={baseColors.label_normal}>
                  평균 {avg.toLocaleString()}원
                </Box>
                <Box width="6px" flexShrink={0} />
                <ColorIcon name="arrow_right_small" size={14} color={baseColors.label_muted_2} />
              </Box>
            </NextLink>
          );
        })}
      </Box>
    </BaseStack>
  );
}

export default RegionStatsHighlight;
